/**
 * FieldDataCapture -- Standalone multi-step wizard for collecting field data.
 *
 * Step 1: Select a location
 * Step 2: Select a collection template
 * Step 3: Capture a photo (if required by template)
 * Step 4: Fill in fields, add notes, and submit
 */

import { useState, useEffect, useRef } from "react";
import {
	Button,
	Select,
	Input,
	InputNumber,
	Rate,
	Switch,
	Spin,
	Steps,
	Result,
	message,
} from "antd";
import {
	EnvironmentOutlined,
	FormOutlined,
	CameraOutlined,
	CheckOutlined,
	ArrowLeftOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";
import {
	getTemplates,
	getTemplateById,
	createRecord,
	type CollectionTemplate,
	type CollectionTemplateWithFields,
	type CollectionTemplateField,
} from "@/services/data-collection-service";
import { uploadPhotoToFirebase } from "@/services/storage-service";
import "./FieldDataCapture.scss";

const { TextArea } = Input;

type WizardStep = "location" | "template" | "capture" | "review" | "done";

const FieldDataCapture = () => {
	const navigate = useNavigate();
	const fileRef = useRef<HTMLInputElement>(null);

	// Wizard state
	const [step, setStep] = useState<WizardStep>("location");

	// Location
	const [locations, setLocations] = useState<any[]>([]);
	const [loadingLocations, setLoadingLocations] = useState(true);
	const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
	const [selectedLocationName, setSelectedLocationName] = useState("");

	// Templates
	const [templates, setTemplatesList] = useState<CollectionTemplate[]>([]);
	const [loadingTemplates, setLoadingTemplates] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState<CollectionTemplateWithFields | null>(null);
	const [loadingTemplate, setLoadingTemplate] = useState(false);

	// Photo
	const [photoFile, setPhotoFile] = useState<File | null>(null);
	const [photoPreview, setPhotoPreview] = useState<string | null>(null);

	// Fields
	const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({});
	const [areaName, setAreaName] = useState("");
	const [notes, setNotes] = useState("");
	const [submitting, setSubmitting] = useState(false);

	// ─── Load locations on mount ───
	useEffect(() => {
		loadLocations();
	}, []);

	const loadLocations = async () => {
		setLoadingLocations(true);
		try {
			const token = await auth.currentUser?.getIdToken();
			const res = await fetch(`${VITE_CORE_API_URL}${CORE_URL}/location`, {
				method: "GET",
				headers: { Authorization: `Bearer ${token}` },
			});
			if (!res.ok) throw new Error("Failed to fetch locations");
			const json = await res.json();
			setLocations(json.data || []);
		} catch (err) {
			console.error("Error loading locations:", err);
			message.error("Failed to load locations");
		} finally {
			setLoadingLocations(false);
		}
	};

	// ─── Load templates when location is selected ───
	const handleLocationSelect = async (locId: number) => {
		const loc = locations.find((l: any) => l.id === locId);
		setSelectedLocationId(locId);
		setSelectedLocationName(loc?.name || loc?.address || "");
		setLoadingTemplates(true);
		try {
			const t = await getTemplates(true);
			setTemplatesList(t);
		} catch {
			message.error("Failed to load templates");
		} finally {
			setLoadingTemplates(false);
		}
		setStep("template");
	};

	// ─── Template selection ───
	const handleTemplateSelect = async (tpl: CollectionTemplate) => {
		setLoadingTemplate(true);
		try {
			const full = await getTemplateById(tpl.id);
			setSelectedTemplate(full);
			setStep(full.requiresPhoto ? "capture" : "review");
		} catch {
			message.error("Failed to load template");
		} finally {
			setLoadingTemplate(false);
		}
	};

	// ─── Photo handling ───
	const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setPhotoFile(file);
		setPhotoPreview(URL.createObjectURL(file));
		setStep("review");
	};

	// ─── Field rendering (same logic as CollectionCapture) ───
	const setFieldValue = (key: string, value: unknown) => {
		setFieldValues((prev) => ({ ...prev, [key]: value }));
	};

	const renderField = (field: CollectionTemplateField) => {
		const value = fieldValues[field.fieldKey];
		switch (field.fieldType) {
			case "TEXT":
				return <Input placeholder={field.config?.placeholder as string || field.fieldLabel} value={(value as string) ?? ""} onChange={(e) => setFieldValue(field.fieldKey, e.target.value)} />;
			case "TEXTAREA":
				return <TextArea placeholder={field.config?.placeholder as string || field.fieldLabel} value={(value as string) ?? ""} onChange={(e) => setFieldValue(field.fieldKey, e.target.value)} rows={3} />;
			case "NUMBER":
				return <InputNumber placeholder={field.fieldLabel} value={value as number} onChange={(v) => setFieldValue(field.fieldKey, v)} addonAfter={field.config?.unit as string} style={{ width: "100%" }} />;
			case "CURRENCY":
				return <InputNumber placeholder={field.fieldLabel} value={value as number} onChange={(v) => setFieldValue(field.fieldKey, v)} prefix="$" style={{ width: "100%" }} />;
			case "SELECT":
				return <Select placeholder={`Select ${field.fieldLabel}`} value={value as string} onChange={(v) => setFieldValue(field.fieldKey, v)} options={(field.options || []).map((o) => ({ label: o, value: o }))} style={{ width: "100%" }} />;
			case "MULTISELECT":
				return <Select mode="multiple" placeholder={`Select ${field.fieldLabel}`} value={(value as string[]) ?? []} onChange={(v) => setFieldValue(field.fieldKey, v)} options={(field.options || []).map((o) => ({ label: o, value: o }))} style={{ width: "100%" }} />;
			case "RATING":
				return <Rate count={(field.config?.max as number) ?? 5} value={(value as number) ?? 0} onChange={(v) => setFieldValue(field.fieldKey, v)} />;
			case "BOOLEAN":
				return <Switch checked={!!value} onChange={(v) => setFieldValue(field.fieldKey, v)} checkedChildren="Yes" unCheckedChildren="No" />;
			default:
				return <Input placeholder={field.fieldLabel} value={(value as string) ?? ""} onChange={(e) => setFieldValue(field.fieldKey, e.target.value)} />;
		}
	};

	// ─── Submit ───
	const handleSubmit = async () => {
		if (!selectedTemplate || !selectedLocationId) return;
		setSubmitting(true);
		try {
			let photoUrl: string | undefined;
			if (photoFile) {
				const folder = `data-collection/${selectedLocationId}`;
				const filename = `${Date.now()}_${photoFile.name}`;
				photoUrl = await uploadPhotoToFirebase(photoFile, folder, filename);
			}
			await createRecord({
				templateId: selectedTemplate.id,
				locationId: selectedLocationId,
				primaryPhotoUrl: photoUrl,
				areaName: areaName || undefined,
				notes: notes || undefined,
				fieldValues: Object.keys(fieldValues).length > 0 ? fieldValues : undefined,
			});
			message.success("Data collected successfully!");
			setStep("done");
		} catch (err: any) {
			message.error(err.message || "Failed to save collection");
		} finally {
			setSubmitting(false);
		}
	};

	// ─── Reset for another capture ───
	const handleStartNew = () => {
		setStep("template");
		setSelectedTemplate(null);
		setPhotoFile(null);
		setPhotoPreview(null);
		setFieldValues({});
		setAreaName("");
		setNotes("");
	};

	// ─── Step index for the Steps component ───
	const stepIndex =
		step === "location" ? 0
		: step === "template" ? 1
		: step === "capture" ? 2
		: step === "review" ? (selectedTemplate?.requiresPhoto ? 3 : 2)
		: 3;

	const stepItems = [
		{ title: "Location" },
		{ title: "Template" },
		...(selectedTemplate?.requiresPhoto !== false ? [{ title: "Photo" }] : []),
		{ title: "Review" },
	];

	return (
		<div className="fdc-page">
			<div className="fdc-header">
				<Button
					type="text"
					icon={<ArrowLeftOutlined />}
					onClick={() => navigate("/forms")}
					className="fdc-back"
				>
					Vision Capture
				</Button>
				<h1>Field Data Capture</h1>
			</div>

			{step !== "done" && (
				<Steps
					current={stepIndex}
					items={stepItems}
					className="fdc-steps"
					size="small"
				/>
			)}

			<div className="fdc-content">
				{/* ─── Step 1: Location ─── */}
				{step === "location" && (
					<div className="fdc-step-location">
						<div className="fdc-step-icon"><EnvironmentOutlined /></div>
						<h2>Select a Location</h2>
						<p>Choose which location you are collecting data at.</p>
						{loadingLocations ? (
							<Spin size="large" />
						) : (
							<Select
								showSearch
								placeholder="Search locations..."
								optionFilterProp="label"
								onChange={(v) => handleLocationSelect(v as number)}
								style={{ width: "100%", maxWidth: 480 }}
								size="large"
								options={locations.map((l: any) => ({
									value: l.id,
									label: l.name || l.address || `Location #${l.id}`,
								}))}
							/>
						)}
					</div>
				)}

				{/* ─── Step 2: Template ─── */}
				{step === "template" && (
					<div className="fdc-step-template">
						<div className="fdc-step-subtitle">
							<EnvironmentOutlined /> {selectedLocationName}
						</div>
						<h2>Select a Template</h2>
						<p>What type of data are you collecting?</p>

						{loadingTemplates || loadingTemplate ? (
							<Spin size="large" />
						) : templates.length === 0 ? (
							<div className="fdc-empty">
								<p>No templates configured yet.</p>
								<Button
									type="primary"
									onClick={() => navigate("/data-collection?tab=templates")}
								>
									Create a Template
								</Button>
							</div>
						) : (
							<div className="fdc-template-grid">
								{templates.map((tpl) => (
									<button
										key={tpl.id}
										type="button"
										className="fdc-template-card"
										onClick={() => handleTemplateSelect(tpl)}
									>
										<div className="fdc-tpl-icon">{tpl.icon || "📋"}</div>
										<div className="fdc-tpl-info">
											<span className="fdc-tpl-name">{tpl.name}</span>
											{tpl.category && <span className="fdc-tpl-cat">{tpl.category}</span>}
											{tpl.description && <span className="fdc-tpl-desc">{tpl.description}</span>}
										</div>
									</button>
								))}
							</div>
						)}

						<Button
							type="text"
							icon={<ArrowLeftOutlined />}
							onClick={() => setStep("location")}
							className="fdc-back-step"
						>
							Change Location
						</Button>
					</div>
				)}

				{/* ─── Step 3: Photo Capture ─── */}
				{step === "capture" && (
					<div className="fdc-step-photo">
						<div className="fdc-step-subtitle">
							{selectedTemplate?.name}
						</div>
						<div className="fdc-photo-prompt">
							<CameraOutlined className="fdc-photo-icon" />
							<h2>Capture a Photo</h2>
							<p>Take a photo of what you are collecting data on.</p>
							<Button
								type="primary"
								size="large"
								icon={<CameraOutlined />}
								onClick={() => fileRef.current?.click()}
							>
								Take Photo
							</Button>
							<input
								ref={fileRef}
								type="file"
								accept="image/*"
								capture="environment"
								style={{ display: "none" }}
								onChange={handlePhotoSelect}
							/>
							<Button
								type="link"
								onClick={() => setStep("review")}
								style={{ marginTop: 12 }}
							>
								Skip photo, enter data manually
							</Button>
						</div>
						<Button
							type="text"
							icon={<ArrowLeftOutlined />}
							onClick={() => setStep("template")}
							className="fdc-back-step"
						>
							Change Template
						</Button>
					</div>
				)}

				{/* ─── Step 4: Review & Fields ─── */}
				{step === "review" && selectedTemplate && (
					<div className="fdc-step-review">
						<div className="fdc-step-subtitle">
							{selectedLocationName} &middot; {selectedTemplate.name}
						</div>

						{photoPreview && (
							<div className="fdc-photo-preview">
								<img src={photoPreview} alt="Captured" />
							</div>
						)}

						<div className="fdc-field-list">
							<div className="fdc-field-item">
								<label className="fdc-field-label">Area / Zone</label>
								<Input
									placeholder="e.g., Lobby, 2nd Floor, East Wing"
									value={areaName}
									onChange={(e) => setAreaName(e.target.value)}
								/>
							</div>

							{selectedTemplate.fields
								.sort((a, b) => a.displayOrder - b.displayOrder)
								.map((field) => (
									<div key={field.id} className="fdc-field-item">
										<label className="fdc-field-label">
											{field.fieldLabel}
											{field.isRequired && <span className="fdc-required">*</span>}
										</label>
										{renderField(field)}
									</div>
								))}

							<div className="fdc-field-item">
								<label className="fdc-field-label">Notes</label>
								<TextArea
									placeholder="Additional observations..."
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									rows={3}
								/>
							</div>
						</div>

						<Button
							type="primary"
							size="large"
							block
							icon={<CheckOutlined />}
							onClick={handleSubmit}
							loading={submitting}
							className="fdc-submit-btn"
						>
							Submit
						</Button>

						<Button
							type="text"
							icon={<ArrowLeftOutlined />}
							onClick={() => setStep(selectedTemplate.requiresPhoto ? "capture" : "template")}
							className="fdc-back-step"
						>
							Back
						</Button>
					</div>
				)}

				{/* ─── Done ─── */}
				{step === "done" && (
					<Result
						status="success"
						title="Data Collected Successfully"
						subTitle={`Submitted to ${selectedLocationName} using ${selectedTemplate?.name}.`}
						extra={[
							<Button
								key="new"
								type="primary"
								icon={<PlusOutlined />}
								onClick={handleStartNew}
							>
								Collect More Data
							</Button>,
							<Button
								key="qc"
								onClick={() => navigate("/quality-check")}
							>
								View in Quality Check
							</Button>,
						]}
					/>
				)}
			</div>
		</div>
	);
};

export default FieldDataCapture;
